import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Table, Button, Form, InputGroup, Alert } from 'react-bootstrap';
import NavigationBar from '../NavigationBar';
import axios from '../axios';
import { formatNumberWithCommas } from '../utilities';

// UP (Utilization Permission) consumption calculator.
// Formula follows the UP-55-2023 sheet the commercial team uses:
//   Box (has height):  sheet = (L + W + 6) x (W + H + 2), two half-blanks (x2),
//                      medium length x flute factor 1.4
//   Flat (divider / top-bottom): sheet = L x W, one piece (medium still x 1.4)
//   kg/pc = sheetL x sheetW x GSM x layers x pieces / 10,000,000, then x qty + wastage%

const DEFAULT_SETTINGS = {
    linerGsm: 151,
    mediumGsm: 120,
    wastage: 8,
    fluteFactor: 1.4,
    lengthAllowance: 6,
    widthAllowance: 2,
    starchPercent: 5,
    starchWastage: 5,
    inkGramPerCarton: 1.25,
    inkWastage: 3,
};

const DEFAULT_PRICES = { liner: 0.555, medium: 0.365, starch: 0.715, ink: 3.65 };
const PRICE_STORAGE_KEY = 'upConsumptionUnitPrices';

const UNIT_TO_CM = { CM: 1, Inch: 2.54, MM: 0.1, Yard: 91.44 };

const toNum = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
};

const round2 = (n) => Math.round(n * 100) / 100;
const fmt = (n, d = 2) => formatNumberWithCommas((Number(n) || 0).toFixed(d));

const parsePly = (spec) => {
    const m = String(spec || '').match(/(\d+)/);
    return m ? parseInt(m[1], 10) : 3;
};

const loadPrices = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(PRICE_STORAGE_KEY));
        return saved ? { ...DEFAULT_PRICES, ...saved } : DEFAULT_PRICES;
    } catch (e) {
        return DEFAULT_PRICES;
    }
};

// Works out the sheet size and kg for one row; also returns the intermediate
// numbers so the Excel export can show the same working as the manual sheet.
export const calcRow = (row, s) => {
    const ply = toNum(row.ply) || 3;
    const liners = Math.ceil(ply / 2);
    const mediums = Math.floor(ply / 2);
    const linerGsm = row.linerGsm !== '' && row.linerGsm != null ? toNum(row.linerGsm) : toNum(s.linerGsm);
    const mediumGsm = row.mediumGsm !== '' && row.mediumGsm != null ? toNum(row.mediumGsm) : toNum(s.mediumGsm);
    const isBox = toNum(row.height) > 0;
    const L = toNum(row.length), W = toNum(row.width), H = toNum(row.height);

    const sheetLength = isBox ? L + W + toNum(s.lengthAllowance) : L;
    const sheetWidth = isBox ? W + H + toNum(s.widthAllowance) : W;
    const pieces = isBox ? 2 : 1;
    const mediumLength = sheetLength * toNum(s.fluteFactor);

    const linerPerPc = (sheetLength * sheetWidth * linerGsm * pieces * liners) / 10000000;
    const mediumPerPc = (mediumLength * sheetWidth * mediumGsm * pieces * mediums) / 10000000;
    const qty = toNum(row.qty);
    const wastageMul = 1 + toNum(s.wastage) / 100;

    return {
        ply, liners, mediums, linerGsm, mediumGsm, isBox, pieces,
        sheetLength, sheetWidth, mediumLength,
        linerPerPc, mediumPerPc,
        linerNet: linerPerPc * qty,
        mediumNet: mediumPerPc * qty,
        linerKg: linerPerPc * qty * wastageMul,
        mediumKg: mediumPerPc * qty * wastageMul,
    };
};

function UpConsumption() {
    const [searchParams] = useSearchParams();
    const [jobInput, setJobInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [pis, setPis] = useState([]); // { piId, jobNumber, partyName, value }
    const [rows, setRows] = useState([]);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [prices, setPrices] = useState(loadPrices);
    const [piValueOverride, setPiValueOverride] = useState('');

    useEffect(() => {
        try {
            localStorage.setItem(PRICE_STORAGE_KEY, JSON.stringify(prices));
        } catch (e) { /* storage unavailable */ }
    }, [prices]);

    useEffect(() => {
        const piId = searchParams.get('pi');
        if (piId) addPi(parseInt(piId, 10));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addPi = async (piId) => {
        if (pis.some(p => p.piId === piId)) {
            setMessage({ type: 'warning', text: 'That PI is already added.' });
            return;
        }
        setLoading(true);
        setMessage(null);
        setCandidates([]);
        try {
            const { data } = await axios.get(`${window.backendUrl}/get_pi_pdf_info/${piId}`);
            const newRows = [];
            let value = 0;
            data.items.forEach(item => {
                item.specifications.forEach(spec => {
                    spec.gsms.forEach(g => {
                        const mul = UNIT_TO_CM[g.unit] || 1;
                        value += toNum(g.orderQuantity) * toNum(g.unitPrice);
                        newRows.push({
                            key: `${piId}-${g.gsmId}`,
                            piId,
                            jobNumber: data.jobNumber,
                            itemName: item.itemName,
                            length: round2(toNum(g.length) * mul),
                            width: round2(toNum(g.width) * mul),
                            height: round2(toNum(g.height) * mul),
                            ply: parsePly(spec.specification),
                            qty: toNum(g.orderQuantity),
                            linerGsm: '',
                            mediumGsm: '',
                        });
                    });
                });
            });
            setPis(prev => prev.some(p => p.piId === piId) ? prev : [...prev, { piId, jobNumber: data.jobNumber, partyName: data.partyName, value }]);
            setRows(prev => [...prev.filter(r => r.piId !== piId), ...newRows]);
            setJobInput('');
        } catch (err) {
            console.error(err);
            setMessage({ type: 'danger', text: 'Could not load that PI.' });
        } finally {
            setLoading(false);
        }
    };

    const handleAddJob = async () => {
        const job = jobInput.trim();
        if (!job) return;
        setLoading(true);
        setMessage(null);
        setCandidates([]);
        try {
            const { data } = await axios.get(`${window.backendUrl}/get_all_pis`, { params: { job_number: job, page: 1 } });
            const unique = [];
            (data.data || []).forEach(p => {
                if (!unique.some(u => u.PiId === p.PiId)) unique.push(p);
            });
            const exact = unique.find(p => String(p.JobNumber).trim().toLowerCase() === job.toLowerCase());
            if (exact) {
                setLoading(false);
                return addPi(exact.PiId);
            }
            if (unique.length === 1) {
                setLoading(false);
                return addPi(unique[0].PiId);
            }
            if (unique.length === 0) {
                setMessage({ type: 'warning', text: `No approved PI found for job "${job}".` });
            } else {
                setCandidates(unique);
            }
        } catch (err) {
            console.error(err);
            setMessage({ type: 'danger', text: 'Search failed.' });
        }
        setLoading(false);
    };

    const removePi = (piId) => {
        setPis(prev => prev.filter(p => p.piId !== piId));
        setRows(prev => prev.filter(r => r.piId !== piId));
    };

    const clearAll = () => {
        setPis([]);
        setRows([]);
        setCandidates([]);
        setMessage(null);
        setPiValueOverride('');
    };

    const updateRow = (key, field, value) => {
        setRows(prev => prev.map(r => (r.key === key ? { ...r, [field]: value } : r)));
    };

    const updateSetting = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

    const computed = useMemo(() => rows.map(r => ({ row: r, calc: calcRow(r, settings) })), [rows, settings]);

    const totals = useMemo(() => {
        const liner = computed.reduce((a, c) => a + c.calc.linerKg, 0);
        const medium = computed.reduce((a, c) => a + c.calc.mediumKg, 0);
        const paper = liner + medium;
        const starchNet = paper * toNum(settings.starchPercent) / 100;
        const starch = starchNet * (1 + toNum(settings.starchWastage) / 100);
        const boxQty = computed.filter(c => c.calc.isBox).reduce((a, c) => a + toNum(c.row.qty), 0);
        const inkNet = boxQty * toNum(settings.inkGramPerCarton) / 1000;
        const ink = inkNet * (1 + toNum(settings.inkWastage) / 100);
        return { liner, medium, paper, starchNet, starch, boxQty, inkNet, ink, grand: paper + starch + ink };
    }, [computed, settings]);

    const valuation = useMemo(() => {
        const lines = [
            { key: 'liner', name: `K. Liner Paper`, kg: totals.liner },
            { key: 'medium', name: `Medium Paper`, kg: totals.medium },
            { key: 'starch', name: 'Starch Powder', kg: totals.starch },
            { key: 'ink', name: 'Printing Ink', kg: totals.ink },
        ].map(l => ({ ...l, price: prices[l.key], value: l.kg * toNum(prices[l.key]) }));
        const total = lines.reduce((a, l) => a + l.value, 0);
        const piValueAuto = pis.reduce((a, p) => a + p.value, 0);
        const piValue = piValueOverride !== '' ? toNum(piValueOverride) : piValueAuto;
        return { lines, total, piValue, piValueAuto, percent: piValue ? (total / piValue) * 100 : 0 };
    }, [totals, prices, pis, piValueOverride]);

    const downloadExcel = async () => {
        const { exportUpConsumptionExcel } = await import('./up_consumption_excel');
        await exportUpConsumptionExcel({ computed, settings, totals, valuation, pis });
    };

    const settingInput = (label, field, suffix, step = 'any') => (
        <Col xs={6} md={3} lg="auto" className="mb-2">
            <Form.Label className="mb-0" style={{ fontSize: '12px' }}>{label}</Form.Label>
            <InputGroup size="sm" style={{ width: '120px' }}>
                <Form.Control type="number" step={step} value={settings[field]} onChange={e => updateSetting(field, e.target.value)} />
                {suffix && <InputGroup.Text>{suffix}</InputGroup.Text>}
            </InputGroup>
        </Col>
    );

    return (
        <>
            <div className="no-print"><NavigationBar /></div>
            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    .up-print input { border: none !important; padding: 0 !important; background: transparent !important; }
                    .up-print .input-group-text { display: none; }
                }
                .up-table td, .up-table th { vertical-align: middle; }
                .up-table input.form-control { padding: 2px 4px; height: 26px; font-size: 13px; }
            `}</style>
            <Container fluid className="up-print" style={{ padding: '16px' }}>
                <h4 className="mb-3">UP Consumption</h4>

                <Row className="no-print align-items-end mb-2">
                    <Col xs={12} md="auto" className="mb-2">
                        <Form.Label className="mb-0" style={{ fontSize: '12px' }}>Job No</Form.Label>
                        <InputGroup size="sm" style={{ width: '280px' }}>
                            <Form.Control
                                placeholder="e.g. 7548/26-N"
                                value={jobInput}
                                onChange={e => setJobInput(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleAddJob(); }}
                            />
                            <Button variant="primary" onClick={handleAddJob} disabled={loading}>{loading ? 'Loading…' : 'Add'}</Button>
                        </InputGroup>
                    </Col>
                    <Col xs="auto" className="mb-2">
                        <Button size="sm" variant="outline-secondary" onClick={clearAll}>Clear</Button>
                    </Col>
                </Row>

                {message && <Alert variant={message.type} className="py-2 no-print">{message.text}</Alert>}
                {candidates.length > 0 && (
                    <Alert variant="info" className="py-2 no-print">
                        Several jobs match — pick one:{' '}
                        {candidates.map(c => (
                            <Button key={c.PiId} size="sm" variant="outline-primary" className="me-2 mb-1" onClick={() => addPi(c.PiId)}>
                                {c.JobNumber} · {c.PartyName}
                            </Button>
                        ))}
                    </Alert>
                )}

                {pis.length > 0 && (
                    <div className="mb-2">
                        {pis.map(p => (
                            <span key={p.piId} className="badge bg-light text-dark border me-2 p-2" style={{ fontSize: '13px' }}>
                                Job {p.jobNumber} · {p.partyName} · ${fmt(p.value)}
                                <i className="fa fa-times ms-2 no-print" style={{ cursor: 'pointer' }} title="Remove job" onClick={() => removePi(p.piId)}></i>
                            </span>
                        ))}
                    </div>
                )}

                <Row className="mb-2 p-2 border rounded bg-light">
                    {settingInput('Liner GSM', 'linerGsm')}
                    {settingInput('Medium GSM', 'mediumGsm')}
                    {settingInput('Paper wastage', 'wastage', '%')}
                    {settingInput('Flute factor', 'fluteFactor')}
                    {settingInput('Length allow.', 'lengthAllowance', 'cm')}
                    {settingInput('Width allow.', 'widthAllowance', 'cm')}
                    {settingInput('Starch', 'starchPercent', '%')}
                    {settingInput('Starch wastage', 'starchWastage', '%')}
                    {settingInput('Ink / carton', 'inkGramPerCarton', 'g')}
                    {settingInput('Ink wastage', 'inkWastage', '%')}
                    <Col xs="auto" className="mb-2 d-flex align-items-end no-print">
                        <Button size="sm" variant="link" onClick={() => setSettings(DEFAULT_SETTINGS)}>Reset</Button>
                    </Col>
                </Row>

                <Table bordered size="sm" className="up-table" style={{ fontSize: '13px' }}>
                    <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                        <tr>
                            <th>SL</th>
                            <th>Job</th>
                            <th>Item</th>
                            <th>Size (cm)</th>
                            <th>Ply</th>
                            <th>Qty</th>
                            <th>Sheet (cm)</th>
                            <th style={{ width: '90px' }}>Liner GSM</th>
                            <th style={{ width: '90px' }}>Medium GSM</th>
                            <th className="text-end">Liner kg</th>
                            <th className="text-end">Medium kg</th>
                            <th className="no-print"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {computed.length === 0 && (
                            <tr><td colSpan={12} className="text-center text-muted py-4">Add a job number above to load its PI lines.</td></tr>
                        )}
                        {computed.map(({ row, calc }, i) => (
                            <tr key={row.key}>
                                <td>{i + 1}</td>
                                <td>{row.jobNumber}</td>
                                <td>{row.itemName}</td>
                                <td>{calc.isBox ? `${row.length}×${row.width}×${row.height}` : `${row.length}×${row.width}`}</td>
                                <td style={{ width: '60px' }}>
                                    <Form.Control type="number" value={row.ply} onChange={e => updateRow(row.key, 'ply', e.target.value)} />
                                </td>
                                <td style={{ width: '90px' }}>
                                    <Form.Control type="number" value={row.qty} onChange={e => updateRow(row.key, 'qty', e.target.value)} />
                                </td>
                                <td>{fmt(calc.sheetLength)} × {fmt(calc.sheetWidth)}{calc.isBox ? ' ×2' : ''}</td>
                                <td>
                                    <Form.Control type="number" placeholder={String(settings.linerGsm)} value={row.linerGsm} onChange={e => updateRow(row.key, 'linerGsm', e.target.value)} />
                                </td>
                                <td>
                                    <Form.Control type="number" placeholder={String(settings.mediumGsm)} value={row.mediumGsm} onChange={e => updateRow(row.key, 'mediumGsm', e.target.value)} />
                                </td>
                                <td className="text-end">{fmt(calc.linerKg)}</td>
                                <td className="text-end">{fmt(calc.mediumKg)}</td>
                                <td className="no-print text-center">
                                    <i className="fa fa-times text-danger" style={{ cursor: 'pointer' }} title="Remove row" onClick={() => setRows(prev => prev.filter(r => r.key !== row.key))}></i>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    {computed.length > 0 && (
                        <tfoot>
                            <tr style={{ fontWeight: 'bold', background: '#f3f6fa' }}>
                                <td colSpan={9} className="text-end">Paper total (incl. {settings.wastage}% wastage)</td>
                                <td className="text-end">{fmt(totals.liner)}</td>
                                <td className="text-end">{fmt(totals.medium)}</td>
                                <td className="no-print"></td>
                            </tr>
                        </tfoot>
                    )}
                </Table>

                {computed.length > 0 && (
                    <>
                        <Row className="mb-3 g-2">
                            {[
                                ['Liner', totals.liner],
                                ['Medium', totals.medium],
                                ['Starch', totals.starch],
                                ['Ink', totals.ink],
                                ['Grand total', totals.grand],
                            ].map(([label, kg]) => (
                                <Col key={label} xs={6} md>
                                    <div className="border rounded p-2 text-center" style={label === 'Grand total' ? { background: '#e8f1ff' } : {}}>
                                        <div style={{ fontSize: '12px' }} className="text-muted">{label}</div>
                                        <div style={{ fontSize: '18px', fontWeight: 600 }}>{fmt(kg)} kg</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>

                        <h6>Valuation</h6>
                        <Table bordered size="sm" className="up-table" style={{ fontSize: '13px', maxWidth: '720px' }}>
                            <thead>
                                <tr>
                                    <th>Material</th>
                                    <th className="text-end">Weight (kg)</th>
                                    <th style={{ width: '140px' }}>Unit price ($/kg)</th>
                                    <th className="text-end">Value ($)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {valuation.lines.map(l => (
                                    <tr key={l.key}>
                                        <td>{l.name}</td>
                                        <td className="text-end">{fmt(l.kg)}</td>
                                        <td>
                                            <Form.Control type="number" step="any" value={prices[l.key]} onChange={e => setPrices(prev => ({ ...prev, [l.key]: e.target.value }))} />
                                        </td>
                                        <td className="text-end">{fmt(l.value)}</td>
                                    </tr>
                                ))}
                                <tr style={{ fontWeight: 'bold' }}>
                                    <td>Total</td>
                                    <td className="text-end">{fmt(totals.grand)}</td>
                                    <td></td>
                                    <td className="text-end">{fmt(valuation.total)}</td>
                                </tr>
                                <tr>
                                    <td colSpan={2}>PI value ($)</td>
                                    <td>
                                        <Form.Control type="number" step="any" placeholder={valuation.piValueAuto.toFixed(2)} value={piValueOverride} onChange={e => setPiValueOverride(e.target.value)} />
                                    </td>
                                    <td className="text-end">{fmt(valuation.piValue)}</td>
                                </tr>
                                <tr style={{ fontWeight: 'bold' }}>
                                    <td colSpan={3}>Raw material value ÷ PI value</td>
                                    <td className="text-end">{fmt(valuation.percent)}%</td>
                                </tr>
                            </tbody>
                        </Table>

                        <div className="no-print mb-4">
                            <Button variant="success" className="me-2" onClick={downloadExcel}>
                                <i className="fa fa-file-excel-o me-1"></i> Download Excel
                            </Button>
                            <Button variant="outline-secondary" onClick={() => window.print()}>
                                <i className="fa fa-print me-1"></i> Print
                            </Button>
                        </div>
                    </>
                )}
            </Container>
        </>
    );
}

export default UpConsumption;
